import { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
    FaHeart,
    FaRegHeart,
    FaMapMarkerAlt,
    FaHotel,
    FaSearch,
    FaStar,
    FaArrowRight,
    FaShieldAlt,
    FaWifi,
    FaSwimmingPool,
    FaUtensils,
    FaRedo,
} from "react-icons/fa";

export default function HotelSearch() {
    const [hotels, setHotels] = useState([]);
    const location = useLocation();
    const searchData = location.state;
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [wishlist, setWishlist] = useState([]);

    /* =====================================================
       WISHLIST
    ===================================================== */

    useEffect(() => {
        const storedWishlist =
            JSON.parse(localStorage.getItem("wishlist")) || [];

        setWishlist(storedWishlist);
    }, []);

    const updateWishlist = (updated) => {
        setWishlist(updated);

        localStorage.setItem(
            "wishlist",
            JSON.stringify(updated)
        );
    };

    const toggleWishlist = (hotel) => {
        const exists = wishlist.find(
            (item) => item.id === hotel.id
        );

        let updated;

        if (exists) {
            updated = wishlist.filter(
                (item) => item.id !== hotel.id
            );
        } else {
            updated = [...wishlist, hotel];
        }

        updateWishlist(updated);
    };

    const isWishlisted = (id) => {
        return wishlist.some(
            (item) => item.id === id
        );
    };

    /* =====================================================
       FETCH HOTELS
    ===================================================== */

    useEffect(() => {
        const fetchHotels = async () => {
            setLoading(true);
            setError(false);

            try {
                const res = await axios.get(
                    "https://marinadubai.com/api/hotels",
                    {
                        params: {
                            destination:
                                searchData?.destination,
                        },
                    }
                );

                setHotels(res.data);
            } catch (err) {
                console.error(err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchHotels();
    }, [searchData]);

    /* =====================================================
       HOTEL NAVIGATION
    ===================================================== */

    const openHotel = (slug) => {
        navigate(`/hotels/${slug}`);
    };

    return (
        <div className="min-h-screen bg-[#F3F8FF]">

            {/* =====================================================
                SEARCH HERO
            ===================================================== */}

            <section className="relative overflow-hidden bg-[#002B45]">

                {/* Decorative Glow */}

                <div className="pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full bg-[#174EA6]/30 blur-3xl" />

                <div className="pointer-events-none absolute -left-32 bottom-[-120px] h-80 w-80 rounded-full bg-[#FFC928]/10 blur-3xl" />

                {/* Decorative Circles */}

                <div className="pointer-events-none absolute right-[12%] top-1/2 hidden h-40 w-40 -translate-y-1/2 rounded-full border border-white/5 lg:block" />

                <div className="pointer-events-none absolute right-[14%] top-1/2 hidden h-24 w-24 -translate-y-1/2 rounded-full border border-[#FFC928]/10 lg:block" />

                {/* Hero Content */}

                <div
                    className="
                        relative
                        z-10
                        mx-auto
                        max-w-7xl
                        px-4
                        pb-10
                        pt-28

                        sm:px-6
                        sm:pb-12
                        sm:pt-32

                        md:px-8
                        md:pb-14
                        md:pt-32

                        lg:px-8
                        lg:pb-14
                        lg:pt-36
                    "
                >

                    <div className="max-w-3xl">

                        {/* Badge */}

                        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#FFC928] backdrop-blur-sm">

                            <FaSearch className="text-xs" />

                            Hotel Search

                        </div>

                        {/* Heading */}

                        <h1 className="mt-4 text-3xl font-extrabold tracking-tight !text-white sm:text-4xl lg:text-5xl">

                            Find Your{" "}

                            <span className="text-[#FFC928]">
                                Perfect Stay
                            </span>

                        </h1>

                        {/* Description */}

                        <p className="mt-3 max-w-2xl text-sm leading-7 text-white/60 sm:text-base">

                            {searchData?.destination
                                ? `Discover comfortable hotels and great stays in ${searchData.destination}.`
                                : "Discover comfortable hotels, great locations and the best available rates."}

                        </p>

                        {/* Search Info */}

                        <div className="mt-6 flex flex-wrap items-center gap-3">

                            <div className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-xs font-semibold text-white/80 backdrop-blur-sm">

                                <FaMapMarkerAlt className="text-[#FFC928]" />

                                {searchData?.destination ||
                                    "Dubai, UAE"}

                            </div>

                            <div className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-xs font-semibold text-white/80 backdrop-blur-sm">

                                <FaHotel className="text-[#FFC928]" />

                                {loading
                                    ? "Searching hotels..."
                                    : `${hotels.length} ${hotels.length === 1
                                        ? "Hotel"
                                        : "Hotels"
                                    } Found`}

                            </div>

                        </div>

                    </div>

                </div>

                {/* Bottom Accent */}

                <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-[#174EA6] via-[#174EA6] to-[#FFC928]" />

            </section>


            {/* =====================================================
                CONTENT
            ===================================================== */}

            <section className="relative overflow-hidden px-4 py-10 sm:px-6 sm:py-12 lg:px-8 lg:py-14">

                {/* Background Decorations */}

                <div className="pointer-events-none absolute -left-40 top-0 h-96 w-96 rounded-full bg-[#174EA6]/5 blur-3xl" />

                <div className="pointer-events-none absolute -right-40 top-1/3 h-96 w-96 rounded-full bg-[#FFC928]/5 blur-3xl" />

                <div className="relative z-10 mx-auto max-w-7xl">

                    {/* =================================================
                        SEARCH HEADER
                    ================================================= */}

                    <div className="mb-8">

                        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">

                            <div>

                                <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#174EA6]">

                                    Search Results

                                </p>

                                <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-[#0F172A] sm:text-3xl">

                                    {searchData?.destination
                                        ? `Hotels in ${searchData.destination}`
                                        : "Available Hotels"}

                                </h2>

                                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">

                                    Find comfortable stays with excellent
                                    locations, useful amenities and competitive
                                    hotel rates.

                                </p>

                            </div>


                            {/* Result Count */}

                            {!loading && !error && hotels.length > 0 && (

                                <div className="flex w-fit items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-3 shadow-[0_10px_35px_rgba(15,23,42,0.05)]">

                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EAF3FF] text-[#174EA6]">

                                        <FaHotel />

                                    </div>

                                    <div>

                                        <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">

                                            Hotels Found

                                        </p>

                                        <p className="text-xl font-extrabold text-[#0F172A]">

                                            {hotels.length}

                                        </p>

                                    </div>

                                </div>

                            )}

                        </div>


                        {/* Destination Search Bar */}

                        <div className="mt-7 flex flex-col gap-3 rounded-[22px] border border-slate-200 bg-white p-3 shadow-[0_10px_35px_rgba(15,23,42,0.05)] sm:flex-row sm:items-center sm:justify-between">

                            <div className="flex items-center gap-3 px-2">

                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#EAF3FF] text-[#174EA6]">

                                    <FaMapMarkerAlt />

                                </div>

                                <div>

                                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">

                                        Searching Destination

                                    </p>

                                    <p className="text-sm font-bold text-[#0F172A]">

                                        {searchData?.destination ||
                                            "Dubai, UAE"}

                                    </p>

                                </div>

                            </div>


                            <button
                                onClick={() =>
                                    navigate("/hotels")
                                }
                                className="
                                    inline-flex
                                    items-center
                                    justify-center
                                    gap-2
                                    rounded-xl
                                    border
                                    border-[#174EA6]/20
                                    bg-[#EAF3FF]
                                    px-5
                                    py-3
                                    text-xs
                                    font-bold
                                    text-[#174EA6]
                                    transition-all
                                    duration-300
                                    hover:border-[#174EA6]
                                    hover:bg-[#174EA6]
                                    hover:text-white
                                "
                            >

                                <FaSearch />

                                Search Again

                            </button>

                        </div>

                    </div>


                    {/* =================================================
                        LOADING
                    ================================================= */}

                    {loading ? (

                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">

                            {[1, 2, 3, 4, 5, 6].map(
                                (item) => (

                                    <div
                                        key={item}
                                        className="
                                            overflow-hidden
                                            rounded-[28px]
                                            border
                                            border-slate-200
                                            bg-white
                                            shadow-[0_10px_35px_rgba(15,23,42,0.05)]
                                        "
                                    >

                                        {/* Image */}

                                        <div className="h-56 animate-pulse bg-slate-200" />

                                        {/* Content */}

                                        <div className="p-6">

                                            <div className="h-5 w-3/4 animate-pulse rounded-lg bg-slate-200" />

                                            <div className="mt-3 h-4 w-1/2 animate-pulse rounded-lg bg-slate-200" />

                                            <div className="mt-5 h-4 w-full animate-pulse rounded-lg bg-slate-200" />

                                            <div className="mt-2 h-4 w-4/5 animate-pulse rounded-lg bg-slate-200" />

                                            <div className="mt-5 flex gap-2">

                                                <div className="h-7 w-16 animate-pulse rounded-full bg-slate-200" />

                                                <div className="h-7 w-16 animate-pulse rounded-full bg-slate-200" />

                                                <div className="h-7 w-20 animate-pulse rounded-full bg-slate-200" />

                                            </div>

                                            <div className="mt-6 h-12 w-full animate-pulse rounded-xl bg-slate-200" />

                                        </div>

                                    </div>

                                )
                            )}

                        </div>


                    ) : error ? (

                        /* =================================================
                           ERROR
                        ================================================= */

                        <div className="overflow-hidden rounded-[30px] border border-red-100 bg-white shadow-[0_15px_50px_rgba(15,23,42,0.06)]">

                            <div className="px-6 py-20 text-center sm:px-10">

                                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[22px] bg-red-50 text-red-500">

                                    <FaRedo className="text-2xl" />

                                </div>

                                <p className="mt-6 text-[10px] font-extrabold uppercase tracking-[0.15em] text-red-500">

                                    Search Error

                                </p>

                                <h3 className="mt-2 text-2xl font-extrabold text-[#0F172A]">

                                    Unable to Load Hotels

                                </h3>

                                <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-500">

                                    Something went wrong while searching for
                                    hotels. Please try again.

                                </p>

                                <button
                                    onClick={() =>
                                        window.location.reload()
                                    }
                                    className="
                                        mt-7
                                        inline-flex
                                        items-center
                                        justify-center
                                        gap-2
                                        rounded-xl
                                        bg-gradient-to-r
                                        from-[#174EA6]
                                        via-[#174EA6]
                                        to-[#FFC928]
                                        px-7
                                        py-3
                                        text-sm
                                        font-bold
                                        text-white
                                        shadow-lg
                                        transition-all
                                        duration-300
                                        hover:-translate-y-0.5
                                        hover:shadow-xl
                                    "
                                >

                                    <FaRedo />

                                    Try Again

                                </button>

                            </div>

                        </div>


                    ) : hotels.length > 0 ? (

                        /* =================================================
                           HOTEL GRID
                        ================================================= */

                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7">

                            {hotels.map((hotel) => (

                                <div
                                    key={hotel.id}
                                    onClick={() =>
                                        openHotel(hotel.slug)
                                    }
                                    className="
                                        group
                                        cursor-pointer
                                        overflow-hidden
                                        rounded-[28px]
                                        border
                                        border-slate-200
                                        bg-white
                                        shadow-[0_10px_35px_rgba(15,23,42,0.06)]
                                        transition-all
                                        duration-500
                                        hover:-translate-y-2
                                        hover:border-[#174EA6]/20
                                        hover:shadow-[0_25px_60px_rgba(15,23,42,0.12)]
                                    "
                                >

                                    {/* =================================================
                                        IMAGE
                                    ================================================= */}

                                    <div
                                        className="
                                            relative
                                            h-56
                                            w-full
                                            overflow-hidden
                                            sm:h-60
                                        "
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            openHotel(
                                                hotel.slug
                                            );
                                        }}
                                    >

                                        <img
                                            src={`https://uaediscountedhotels.com/uploads/hotels/${hotel.image}`}
                                            alt={hotel.name}
                                            className="
                                                h-full
                                                w-full
                                                object-cover
                                                transition-transform
                                                duration-700
                                                group-hover:scale-110
                                            "
                                        />

                                        {/* Image Overlay */}

                                        <div className="absolute inset-0 bg-gradient-to-t from-[#002B45]/90 via-[#002B45]/10 to-transparent" />


                                        {/* Top Gradient */}

                                        <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/30 to-transparent" />


                                        {/* =================================================
                                            LOCATION BADGE
                                        ================================================= */}

                                        <div className="absolute left-4 top-4 z-20">

                                            <span className="inline-flex max-w-[210px] items-center gap-1.5 rounded-full border border-white/30 bg-white/95 px-3 py-1.5 text-xs font-bold text-[#0F172A] shadow-lg backdrop-blur-md">

                                                <FaMapMarkerAlt className="shrink-0 text-[#174EA6]" />

                                                <span className="truncate">

                                                    {hotel.location ||
                                                        "Dubai, UAE"}

                                                </span>

                                            </span>

                                        </div>


                                        {/* =================================================
                                            WISHLIST
                                        ================================================= */}

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();

                                                toggleWishlist(
                                                    hotel
                                                );
                                            }}
                                            className={`
                                                absolute
                                                right-4
                                                top-4
                                                z-20
                                                flex
                                                h-11
                                                w-11
                                                items-center
                                                justify-center
                                                rounded-full
                                                border
                                                border-white/40
                                                bg-white/95
                                                shadow-lg
                                                backdrop-blur-md
                                                transition-all
                                                duration-300
                                                hover:scale-110
                                                ${isWishlisted(
                                                hotel.id
                                            )
                                                    ? "text-red-500"
                                                    : "text-slate-500 hover:bg-[#EAF3FF] hover:text-[#174EA6]"
                                                }
                                            `}
                                            aria-label={
                                                isWishlisted(
                                                    hotel.id
                                                )
                                                    ? "Remove from wishlist"
                                                    : "Add to wishlist"
                                            }
                                        >

                                            {isWishlisted(
                                                hotel.id
                                            ) ? (
                                                <FaHeart className="text-base" />
                                            ) : (
                                                <FaRegHeart className="text-base" />
                                            )}

                                        </button>


                                        {/* =================================================
                                            RATING
                                        ================================================= */}

                                        <div className="absolute bottom-4 left-4 z-20">

                                            <div className="flex items-center gap-2 rounded-xl border border-white/30 bg-white/95 px-3 py-2 shadow-lg backdrop-blur-md">

                                                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#FFC928] text-[#002B45]">

                                                    <FaStar className="text-[10px]" />

                                                </div>

                                                <div>

                                                    <div className="flex items-center gap-1">

                                                        <span className="text-sm font-extrabold text-[#0F172A]">

                                                            {hotel.rating ||
                                                                "4.5"}

                                                        </span>

                                                        <span className="text-[10px] font-semibold text-slate-400">

                                                            / 5

                                                        </span>

                                                    </div>

                                                    <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">

                                                        Excellent

                                                    </p>

                                                </div>

                                            </div>

                                        </div>


                                        {/* =================================================
                                            PRICE
                                        ================================================= */}

                                        <div className="absolute bottom-4 right-4 z-20 rounded-2xl bg-gradient-to-r from-[#174EA6] to-[#002B45] px-4 py-2.5 text-white shadow-xl">

                                            <p className="text-[9px] font-semibold uppercase tracking-wider text-white/60">

                                                From

                                            </p>

                                            <p className="mt-0.5 text-sm font-extrabold">

                                                AED{" "}
                                                {Math.round(
                                                    hotel.price_per_night
                                                )}

                                                <span className="ml-1 text-[10px] font-medium text-white/60">

                                                    / night

                                                </span>

                                            </p>

                                        </div>

                                    </div>


                                    {/* =================================================
                                        CONTENT
                                    ================================================= */}

                                    <div className="p-5 sm:p-6">

                                        {/* Hotel Name */}

                                        <h3
                                            onClick={(e) => {
                                                e.stopPropagation();

                                                openHotel(
                                                    hotel.slug
                                                );
                                            }}
                                            className="
                                                line-clamp-1
                                                text-lg
                                                font-extrabold
                                                text-[#0F172A]
                                                transition-colors
                                                duration-300
                                                group-hover:text-[#174EA6]
                                                sm:text-xl
                                            "
                                        >

                                            {hotel.name}

                                        </h3>


                                        {/* Location */}

                                        <div className="mt-2 flex items-center gap-2 text-slate-500">

                                            <FaMapMarkerAlt className="shrink-0 text-sm text-[#174EA6]" />

                                            <span className="line-clamp-1 text-sm">

                                                {hotel.location ||
                                                    "Dubai, UAE"}

                                            </span>

                                        </div>


                                        {/* Divider */}

                                        <div className="my-4 h-px bg-slate-100" />


                                        {/* Description */}

                                        <p className="min-h-[48px] line-clamp-2 text-sm leading-6 text-slate-500">

                                            {hotel.short_description?.replace(
                                                /<[^>]*>/g,
                                                ""
                                            )}

                                        </p>


                                        {/* =================================================
                                            AMENITIES
                                        ================================================= */}

                                        <div className="mt-5 flex flex-wrap gap-2">

                                            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#174EA6]/10 bg-[#EAF3FF] px-3 py-1.5 text-[10px] font-bold text-[#174EA6]">

                                                <FaSwimmingPool />

                                                Pool

                                            </span>

                                            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#174EA6]/10 bg-[#EAF3FF] px-3 py-1.5 text-[10px] font-bold text-[#174EA6]">

                                                <FaWifi />

                                                WiFi

                                            </span>

                                            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#174EA6]/10 bg-[#EAF3FF] px-3 py-1.5 text-[10px] font-bold text-[#174EA6]">

                                                <FaUtensils />

                                                Restaurant

                                            </span>

                                        </div>


                                        {/* =================================================
                                            BUTTON
                                        ================================================= */}

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();

                                                openHotel(
                                                    hotel.slug
                                                );
                                            }}
                                            className="
                                                mt-6
                                                flex
                                                w-full
                                                items-center
                                                justify-center
                                                gap-2
                                                rounded-xl
                                                bg-gradient-to-r
                                                from-[#174EA6]
                                                via-[#174EA6]
                                                to-[#FFC928]
                                                px-5
                                                py-3.5
                                                text-sm
                                                font-extrabold
                                                text-white
                                                shadow-md
                                                transition-all
                                                duration-300
                                                hover:-translate-y-0.5
                                                hover:shadow-xl
                                            "
                                        >

                                            View Hotel Details

                                            <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />

                                        </button>

                                    </div>

                                </div>

                            ))}

                        </div>


                    ) : (

                        /* =================================================
                           EMPTY STATE
                        ================================================= */

                        <div className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_15px_50px_rgba(15,23,42,0.06)]">

                            <div className="px-6 py-20 text-center sm:px-10">

                                {/* Icon */}

                                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[22px] bg-[#EAF3FF] text-[#174EA6]">

                                    <FaHotel className="text-2xl" />

                                </div>


                                <p className="mt-6 text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#174EA6]">

                                    No Results

                                </p>


                                <h3 className="mt-2 text-2xl font-extrabold text-[#0F172A]">

                                    No Hotels Found

                                </h3>


                                <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-500">

                                    We couldn't find any hotels matching
                                    your search.

                                    <br className="hidden sm:block" />

                                    Try another destination or explore all
                                    available hotels.

                                </p>


                                {/* Buttons */}

                                <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">

                                    <button
                                        onClick={() =>
                                            navigate("/hotels")
                                        }
                                        className="
                                            inline-flex
                                            w-full
                                            items-center
                                            justify-center
                                            gap-2
                                            rounded-xl
                                            bg-gradient-to-r
                                            from-[#174EA6]
                                            via-[#174EA6]
                                            to-[#FFC928]
                                            px-7
                                            py-3.5
                                            text-sm
                                            font-bold
                                            text-white
                                            shadow-lg
                                            transition-all
                                            duration-300
                                            hover:-translate-y-0.5
                                            hover:shadow-xl
                                            sm:w-auto
                                        "
                                    >

                                        Explore All Hotels

                                        <FaArrowRight className="text-xs" />

                                    </button>


                                    <button
                                        onClick={() =>
                                            navigate("/")
                                        }
                                        className="
                                            inline-flex
                                            w-full
                                            items-center
                                            justify-center
                                            rounded-xl
                                            border
                                            border-slate-200
                                            bg-white
                                            px-7
                                            py-3.5
                                            text-sm
                                            font-bold
                                            text-[#0F172A]
                                            transition-all
                                            duration-300
                                            hover:border-[#174EA6]/30
                                            hover:bg-[#EAF3FF]
                                            hover:text-[#174EA6]
                                            sm:w-auto
                                        "
                                    >

                                        Back to Home

                                    </button>

                                </div>

                            </div>

                        </div>

                    )}


                    {/* =================================================
                        TRUST / SECURITY STRIP
                    ================================================= */}

                    {!loading && !error && hotels.length > 0 && (

                        <div className="mt-8 grid gap-4 sm:grid-cols-3">

                            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_8px_25px_rgba(15,23,42,0.04)]">

                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EAF3FF] text-[#174EA6]">

                                    <FaShieldAlt />

                                </div>

                                <div>

                                    <p className="text-xs font-extrabold text-[#0F172A]">

                                        Trusted Stays

                                    </p>

                                    <p className="mt-0.5 text-[10px] text-slate-400">

                                        Carefully selected hotels

                                    </p>

                                </div>

                            </div>


                            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_8px_25px_rgba(15,23,42,0.04)]">

                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FFF8D9] text-[#D49E00]">

                                    <FaStar />

                                </div>

                                <div>

                                    <p className="text-xs font-extrabold text-[#0F172A]">

                                        Great Locations

                                    </p>

                                    <p className="mt-0.5 text-[10px] text-slate-400">

                                        Convenient places to stay

                                    </p>

                                </div>

                            </div>


                            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_8px_25px_rgba(15,23,42,0.04)]">

                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EAF3FF] text-[#174EA6]">

                                    <FaHotel />

                                </div>

                                <div>

                                    <p className="text-xs font-extrabold text-[#0F172A]">

                                        Easy Booking

                                    </p>

                                    <p className="mt-0.5 text-[10px] text-slate-400">

                                        Simple hotel reservations

                                    </p>

                                </div>

                            </div>

                        </div>

                    )}

                </div>

            </section>

        </div>
    );
}